import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SessionProvider } from "@/state/session";
import { AppShell } from "@/components/shell/AppShell";

import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ProductAnalyzer from "@/pages/ProductAnalyzer";
import SellerMap from "@/pages/SellerMap";
import DistributorMap from "@/pages/DistributorMap";
import ProductFinder from "@/pages/ProductFinder";
import StorefrontHunter from "@/pages/StorefrontHunter";
import TopSellers from "@/pages/TopSellers";
import DealFlow from "@/pages/DealFlow";
import AutoSourcing from "@/pages/AutoSourcing";
import CatalogAnalyzer from "@/pages/CatalogAnalyzer";
import ReceiptIntelligence from "@/pages/ReceiptIntelligence";
import Inventory from "@/pages/Inventory";
import SalesProfit from "@/pages/SalesProfit";
import PPC from "@/pages/PPC";
import WFS from "@/pages/WFS";
import SavedLeads from "@/pages/SavedLeads";
import { Watchlists, Alerts } from "@/pages/WatchlistsAlerts";
import { Purchases, Sources, ScanHistory, Exports } from "@/pages/WorkspacePages";
import { Team } from "@/pages/TeamSettings";
import { SettingsPage as Settings } from "@/pages/SettingsPage";
import ProfilePage from "@/pages/Profile";
import SidePanel from "@/extension/SidePanel";

function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Navigate to="/signin" replace />} />
          <Route path="/signin" element={<AuthPage mode="signin" />} />
          <Route path="/signup" element={<AuthPage mode="signup" />} />
          <Route path="/onboarding" element={<AuthPage mode="onboarding" />} />
          <Route path="/extension" element={<SidePanel />} />

          <Route path="/app" element={<AppShell />}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="analyzer" element={<ProductAnalyzer />} />

            {/* Marketplace Intelligence */}
            <Route path="marketplace/seller-map" element={<SellerMap />} />
            <Route path="marketplace/distributor-map" element={<DistributorMap />} />

            {/* Product Research */}
            <Route path="research/quick-winner" element={<DealFlow />} />
            {/* Backward-compatible alias for the pre-rename Deal Flow URL. */}
            <Route path="research/deal-flow" element={<Navigate to="/app/research/quick-winner" replace />} />
            <Route path="research/auto-sourcing" element={<AutoSourcing />} />
            <Route path="research/finder" element={<ProductFinder />} />
            <Route path="research/storefront-hunter" element={<StorefrontHunter />} />
            <Route path="research/top" element={<DealFlow title="Top-Selling Products" />} />
            <Route path="research/reverse" element={<DealFlow title="Reverse Sourcing" />} />
            <Route path="research/saved" element={<DealFlow title="Saved Searches" />} />
            {/* legacy ProductFinder/TopSellers still available if linked directly */}
            <Route path="research/finder-legacy" element={<ProductFinder />} />
            <Route path="dashboard/product-finder" element={<ProductFinder />} />
            <Route path="research/top-legacy" element={<TopSellers />} />

            {/* Leads */}
            <Route path="leads" element={<SavedLeads />} />
            <Route path="leads/watchlist" element={<Watchlists />} />
            <Route path="leads/collections" element={<SavedLeads />} />
            <Route path="leads/alerts" element={<Alerts />} />

            {/* Sourcing */}
            <Route path="sourcing/oa" element={<CatalogAnalyzer />} />
            <Route path="sourcing/scanner" element={<Sources />} />
            <Route path="sourcing/catalogs" element={<CatalogAnalyzer />} />
            <Route path="sourcing/suppliers" element={<Sources />} />

            {/* Inventory */}
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/replenishment" element={<Inventory />} />
            <Route path="inventory/wfs" element={<WFS />} />
            <Route path="inventory/po" element={<Purchases />} />

            {/* Sales & Profit */}
            <Route path="sales" element={<SalesProfit />} />
            <Route path="sales/profit" element={<SalesProfit />} />
            <Route path="sales/expenses" element={<Purchases />} />
            <Route path="sales/ppc" element={<PPC />} />
            <Route path="sales/payouts" element={<SalesProfit />} />

            {/* Receipts */}
            <Route path="receipts" element={<ReceiptIntelligence />} />
            <Route path="receipts/scan" element={<ReceiptIntelligence />} />
            <Route path="receipts/transactions" element={<ReceiptIntelligence />} />
            <Route path="receipts/reconciliation" element={<ReceiptIntelligence />} />

            {/* legacy back-compat */}
            <Route path="finder" element={<ProductFinder />} />
            <Route path="catalog" element={<CatalogAnalyzer />} />
            <Route path="purchases" element={<Purchases />} />
            <Route path="wfs" element={<WFS />} />
            <Route path="ppc" element={<PPC />} />
            <Route path="watchlists" element={<Watchlists />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="sources" element={<Sources />} />
            <Route path="history" element={<ScanHistory />} />
            <Route path="exports" element={<Exports />} />
            <Route path="team" element={<Team />} />
            <Route path="profile" element={<ProfilePage />} />

            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}

export default App;
